import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { type AuthUser } from "wasp/auth";
import {
  getAdminBlogPosts,
  getBlogCategories,
  publishBlogPost,
  unpublishBlogPost,
  deleteBlogPost,
  useQuery,
  useAction,
} from "wasp/client/operations";
import DefaultLayout from "../../layout/DefaultLayout";
import Breadcrumb from "../../layout/Breadcrumb";
import { Button } from "../../../client/components/ui/button";
import { Badge } from "../../../client/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../client/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../../client/components/ui/dialog";
import { useToast } from "../../../client/hooks/use-toast";
import { Plus, Pencil, Trash2, Eye, EyeOff, FileText } from "lucide-react";
import useTranslation from "../../../client/hooks/useTranslation";

export default function AdminBlogPage({ user }: { user: AuthUser }) {
  const t = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [statusFilter, setStatusFilter] = useState<"ALL" | "DRAFT" | "PUBLISHED">("ALL");
  const [categoryFilter, setCategoryFilter] = useState<string | undefined>(undefined);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; title: string } | null>(null);

  const { data: posts, refetch } = useQuery(getAdminBlogPosts, {
    status: statusFilter,
    categoryId: categoryFilter,
  });
  const { data: categories } = useQuery(getBlogCategories, undefined);

  const publish = useAction(publishBlogPost);
  const unpublish = useAction(unpublishBlogPost);
  const deletePost = useAction(deleteBlogPost);

  const handleTogglePublish = async (post: any) => {
    try {
      if (post.status === "PUBLISHED") {
        await unpublish({ id: post.id });
        toast({ title: t.adminBlog.postUnpublished });
      } else {
        await publish({ id: post.id });
        toast({ title: t.adminBlog.postPublished });
      }
      refetch();
    } catch (e: any) {
      toast({ title: e.message, variant: "destructive" });
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deletePost({ id: deleteTarget.id });
      toast({ title: t.adminBlog.postDeleted });
      setDeleteTarget(null);
      refetch();
    } catch (e: any) {
      toast({ title: e.message, variant: "destructive" });
    }
  };

  return (
    <DefaultLayout user={user}>
      <Breadcrumb pageName={t.adminBlog.title} />
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as any)}>
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">{t.adminBlog.statusAll}</SelectItem>
              <SelectItem value="DRAFT">{t.adminBlog.statusDraft}</SelectItem>
              <SelectItem value="PUBLISHED">{t.adminBlog.statusPublished}</SelectItem>
            </SelectContent>
          </Select>
          {categories && categories.length > 0 && (
            <Select
              value={categoryFilter ?? "ALL"}
              onValueChange={(v) => setCategoryFilter(v === "ALL" ? undefined : v)}
            >
              <SelectTrigger className="w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">{t.blog.allCategories}</SelectItem>
                {categories.map((cat: any) => (
                  <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Link to="/admin/blog/categories">
            <Button variant="outline" size="sm">{t.adminBlog.manageCategories}</Button>
          </Link>
        </div>
        <Button onClick={() => navigate("/admin/blog/new")}>
          <Plus className="w-4 h-4 mr-2" />
          {t.adminBlog.newPost}
        </Button>
      </div>

      <div className="rounded-xl border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-card border-b border-border">
            <tr>
              <th className="text-left px-4 py-3 text-muted-foreground font-medium">{t.adminBlog.titleField}</th>
              <th className="text-left px-4 py-3 text-muted-foreground font-medium">{t.adminBlog.categoryField}</th>
              <th className="text-left px-4 py-3 text-muted-foreground font-medium">{t.adminBlog.tagsField}</th>
              <th className="text-left px-4 py-3 text-muted-foreground font-medium">Statut</th>
              <th className="text-left px-4 py-3 text-muted-foreground font-medium">Date</th>
              <th className="text-right px-4 py-3 text-muted-foreground font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {(posts ?? []).map((post: any) => (
              <tr key={post.id} className="border-b border-border/50 hover:bg-card/50">
                <td className="px-4 py-3 font-medium text-foreground max-w-xs truncate">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
                    <span className="truncate">{post.title}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-muted-foreground">{post.category?.name ?? "—"}</td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    {post.tags.slice(0, 3).map((tag: string) => (
                      <Badge key={tag} variant="outline" className="text-xs">{tag}</Badge>
                    ))}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <Badge variant={post.status === "PUBLISHED" ? "default" : "secondary"}>
                    {post.status === "PUBLISHED" ? t.blog.published : t.blog.draft}
                  </Badge>
                </td>
                <td className="px-4 py-3 text-muted-foreground text-xs">
                  {post.publishedAt ? new Date(post.publishedAt).toLocaleDateString("fr-FR") : "—"}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/blog/${post.id}/edit`)}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleTogglePublish(post)}>
                      {post.status === "PUBLISHED" ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => setDeleteTarget({ id: post.id, title: post.title })}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
            {(posts ?? []).length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                  {t.blog.noPostsFound}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.adminBlog.confirmDelete}</DialogTitle>
          </DialogHeader>
          <p className="text-muted-foreground text-sm">"{deleteTarget?.title}"</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Annuler</Button>
            <Button variant="destructive" onClick={handleDelete}>{t.adminBlog.deletePost}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DefaultLayout>
  );
}
